import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationsRead,
  type AdminNotificationRow,
} from "../api/endpoints/notifications"
import {
  BusinessPageHeader,
  Button,
  ContentCard,
  EmptyState,
  NotificationItem,
  SegmentedTabs,
  StatusPill,
} from "../components/ui"
import { formatDateTime } from "../utils/formatting"
import { getAdminNotificationPresentation } from "../utils/notifications"

export function NotificationsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [filter, setFilter] = useState<"" | "true" | "false">("")
  const [page, setPage] = useState(1)

  const notificationsQuery = useQuery({
    queryKey: ["notifications", filter, page],
    queryFn: () =>
      listNotifications({
        ...(filter ? { is_read: filter } : {}),
        page: String(page),
        per_page: "50",
      }),
  })

  const unreadCountQuery = useQuery({
    queryKey: ["unread-count"],
    queryFn: () => getUnreadCount(),
  })

  const markReadMutation = useMutation({
    mutationFn: (ids: string[]) => markNotificationsRead(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      queryClient.invalidateQueries({ queryKey: ["unread-count"] })
    },
  })

  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      queryClient.invalidateQueries({ queryKey: ["unread-count"] })
    },
  })

  const rows = notificationsQuery.data?.data ?? []
  const meta = notificationsQuery.data?.meta
  const unreadCount = unreadCountQuery.data?.data?.unreadCount ?? 0
  const pageTitle =
    filter === "false" ? "Unread notifications" : filter === "true" ? "Read notifications" : "All notifications"
  const pageSubtitle =
    filter === "false"
      ? "Focus on pending alerts that still need attention."
      : filter === "true"
        ? "Previously reviewed operational updates and audit events."
        : "System alerts, workflow updates, and borrower events across the platform."

  return (
    <div className="flex flex-col gap-6">
      <BusinessPageHeader
        eyebrow="Inbox"
        title="Notifications"
        subtitle="Track system alerts, workflow changes, and client events in one place without losing context."
        meta={
          <>
            <StatusPill tone={unreadCount > 0 ? "info" : "neutral"}>
              {unreadCount} unread
            </StatusPill>
            <StatusPill tone="neutral">{meta?.total ?? rows.length} in current view</StatusPill>
          </>
        }
        actions={
          <Button
            variant="secondary"
            onClick={() => markAllMutation.mutate()}
            disabled={markAllMutation.isPending || unreadCount === 0}
          >
            Mark all read
          </Button>
        }
      />

      <SegmentedTabs
        value={filter}
        options={[
          { value: "", label: "All" },
          { value: "false", label: "Unread" },
          { value: "true", label: "Read" },
        ]}
        onChange={(value) => {
          setFilter(value)
          setPage(1)
        }}
      />

      <ContentCard
        title={pageTitle}
        subtitle={pageSubtitle}
        actions={
          meta ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                Page {meta.page} of {meta.totalPages}
              </span>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                {meta.total} total
              </span>
            </div>
          ) : null
        }
      >
        {notificationsQuery.isLoading ? (
          <div className="space-y-3">
            <div className="h-28 animate-pulse rounded-2xl border border-slate-200 bg-slate-50" />
            <div className="h-28 animate-pulse rounded-2xl border border-slate-200 bg-slate-50" />
            <div className="h-28 animate-pulse rounded-2xl border border-slate-200 bg-slate-50" />
          </div>
        ) : notificationsQuery.isError ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
            {(notificationsQuery.error as Error).message}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No notifications"
            description="New events will appear here when alerts, tickets, or client workflow updates are generated."
          />
        ) : (
          <div className="space-y-3">
            {rows.map((row: AdminNotificationRow) => {
              const presentation = getAdminNotificationPresentation(row)
              const target = presentation.target

              return (
                <NotificationItem
                  key={row.notificationId}
                  body={row.body}
                  categoryLabel={presentation.categoryLabel}
                  eventLabel={presentation.eventLabel}
                  eventTone={presentation.eventTone}
                  isRead={row.isRead}
                  targetLabel={target?.label}
                  timestampLabel={formatDateTime(row.createdAt)}
                  title={row.title}
                  onMarkRead={
                    !row.isRead ? () => markReadMutation.mutate([row.notificationId]) : undefined
                  }
                  onOpen={
                    target
                      ? () => {
                          if (!row.isRead) {
                            markReadMutation.mutate([row.notificationId])
                          }
                          navigate(target.path)
                        }
                      : undefined
                  }
                />
              )
            })}
          </div>
        )}

        {meta && meta.totalPages > 1 ? (
          <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-500">
              Page {meta.page} of {meta.totalPages} ({meta.total} total)
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                disabled={page <= 1}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                disabled={page >= meta.totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </ContentCard>
    </div>
  )
}
